/**
 * @file SettingsSectionNav.tsx
 * @description Список разделов «Настроек сайта» (phase19.16 Р1; SC56;
 * uiux-design-r1 §4): на широком экране — колонка 240px без описаний, на
 * телефоне и планшете — отдельный экран, строки по 64. Раздел с ошибкой —
 * со значком ✕ (сведения о продавце не заполнены).
 */

import { ChevronRight, CircleX } from "lucide-react";

import { cn } from "@/lib/utils";

export interface SettingsSection<K extends string> {
  key: K;
  label: string;
  /** Раздел с ошибкой, мешающей публикации. */
  error?: boolean;
}

interface Props<K extends string> {
  sections: readonly SettingsSection<K>[];
  current: K | null;
  onSelect: (key: K) => void;
  /** `column` — колонка широкого экрана, `screen` — список на телефоне. */
  variant: "column" | "screen";
}

export function SettingsSectionNav<K extends string>({ sections, current, onSelect, variant }: Props<K>) {
  return (
    <nav aria-label="Разделы настроек сайта" className={variant === "screen" ? "rounded-xl bg-surface-lowest [box-shadow:inset_0_0_0_1px_var(--border)]" : undefined}>
      <ul className={cn("flex flex-col", variant === "screen" && "divide-y divide-[var(--border)]")}>
        {sections.map((section) => {
          const active = section.key === current;
          return (
            <li key={section.key}>
              <button
                type="button"
                aria-current={active ? "page" : undefined}
                onClick={() => onSelect(section.key)}
                className={cn(
                  "flex w-full items-center gap-2 px-3 text-left text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  variant === "screen" ? "min-h-16" : "min-h-11 rounded-lg",
                  active ? "bg-accent text-accent-foreground" : "text-on-surface hover:bg-surface-low",
                )}
              >
                <span className="flex-1">{section.label}</span>
                {section.error ? <CircleX aria-label="Есть ошибка" className="size-4 text-destructive" /> : null}
                {variant === "screen" ? <ChevronRight aria-hidden className="size-4 text-on-surface-variant" /> : null}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
